/**
 * users.js — Users/profile/admin domain service.
 * Profile, user CRUD, organization CRUD, site CRUD, password management.
 */
import {
  toApiUrl,
  toSameOriginApiIfProxied,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
  toUserFacingErrorMessage,
  normalizeApiList,
  normalizeOrganizationRows,
  compactObject,
  requestJson,
  extractAnyId,
  normalizeRoleToBackendRoleId,
  setProfileEndpointMarkedMissing,
  getStoredProfileCache,
  setStoredProfileCache,
  getProfileFallback,
  getStoredAccessToken,
  decodeJwtPayload,
  buildProfileFromJwtPayload,
  formatPasswordVerificationFailureMessage,
  verifyPasswordWithLoginProbe,
} from './_core';

async function resolveEmailForAuthenticatedSession(token) {
  const access = token || getStoredAccessToken();
  const fromJwt = buildProfileFromJwtPayload(decodeJwtPayload(access));
  if (fromJwt && typeof fromJwt.email === 'string' && fromJwt.email.includes('@')) {
    return fromJwt.email.trim();
  }
  try {
    const profile = await getProfile(token);
    if (profile && typeof profile.email === 'string' && profile.email.includes('@')) {
      return profile.email.trim();
    }
  } catch {
    /* ignore */
  }
  return '';
}

export async function getProfile(token = null) {
  try {
    const response = await fetchWithAuthRetry(toApiUrl('/profile/'), jsonFetchOptions(token), token);
    if (!response.ok) {
      if (response.status === 404) {
        setProfileEndpointMarkedMissing(true);
        const fallback = getProfileFallback(token);
        if (fallback) return fallback;
      }
      throw new Error(
        toUserFacingErrorMessage(
          `Profile fetch failed (${response.status})`,
          'Unable to load profile. Please try again.',
          { status: response.status }
        )
      );
    }
    setProfileEndpointMarkedMissing(false);
    const data = await parseJsonOrThrow(response, 'Profile');
    if (data && typeof data === 'object') {
      const normalized = {
        ...data,
        name: data.name ?? data.display_name ?? '',
        display_name: data.display_name ?? data.name ?? '',
        email: data.email ?? '',
        timezone: data.timezone ?? '',
        notification_settings: Array.isArray(data.notification_settings) ? data.notification_settings : [],
      };
      setStoredProfileCache(normalized);
      return normalized;
    }
    return data;
  } catch (error) {
    const fallback = getProfileFallback(token);
    if (fallback) return fallback;
    throw error;
  }
}

export async function updateProfile(payload, token = null) {
  const response = await fetchWithAuthRetry(toApiUrl('/profile/'), {
    method: 'PATCH',
    ...jsonFetchOptions(token),
    body: JSON.stringify(payload),
  }, token);
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Profile update failed (${response.status})`);
    throw new Error(msg);
  }
  const data = await parseJsonOrThrow(response, 'Profile');
  if (data && typeof data === 'object') {
    setProfileEndpointMarkedMissing(false);
    setStoredProfileCache({
      ...getStoredProfileCache(),
      ...data,
      name: data.name ?? data.display_name ?? payload?.name ?? '',
      display_name: data.display_name ?? data.name ?? payload?.name ?? '',
      email: data.email ?? payload?.email ?? '',
      timezone: data.timezone ?? payload?.timezone ?? '',
      notification_settings: Array.isArray(data.notification_settings)
        ? data.notification_settings
        : getStoredProfileCache()?.notification_settings ?? [],
    });
  }
  return data;
}

export async function verifyProfileCurrentPassword(currentPassword, token = null) {
  const pwd = String(currentPassword ?? '').trim();
  if (!pwd) {
    throw new Error('Password is required.');
  }

  const profileResponse = await fetchWithAuthRetry(
    toApiUrl('/profile/'),
    {
      method: 'PATCH',
      ...jsonFetchOptions(token),
      body: JSON.stringify({
        verify_current_password_only: true,
        current_password: pwd,
      }),
    },
    token
  );

  if (!profileResponse.ok) {
    const msg = await readApiErrorMessage(
      profileResponse,
      `Password verification failed (${profileResponse.status})`
    );
    throw new Error(formatPasswordVerificationFailureMessage(msg));
  }

  let profileData;
  try {
    profileData = await parseJsonOrThrow(profileResponse, 'Profile');
  } catch {
    profileData = null;
  }

  if (profileData && profileData.password_verification_ok === true) {
    return;
  }

  const email = await resolveEmailForAuthenticatedSession(token);
  if (!email) {
    throw new Error(
      'Password could not be verified for this login: no email is available from your session token or profile API. Redeploy the API with profile password verification, or ensure your access token includes an email claim.'
    );
  }

  await verifyPasswordWithLoginProbe(email, pwd);
}

export async function listUsersAdmin(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(toApiUrl(`/users/${qs}`), jsonFetchOptions(token), token);

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Users list failed (${response.status})`);
    throw new Error(msg);
  }

  return normalizeApiList(await parseJsonOrThrow(response, 'Users'));
}

export const listOrganizations = (token = null, query = null) =>
  requestJson('GET', '/org/', null, token, query).then((data) => normalizeOrganizationRows(data));

export async function listOrganizationsAll(token = null, query = null) {
  const baseQuery = query && typeof query === 'object' ? { ...query } : {};
  const firstQs = new URLSearchParams(baseQuery).toString();
  const firstUrl = toApiUrl(`/org/${firstQs ? `?${firstQs}` : ''}`);

  const firstResponse = await fetchWithAuthRetry(firstUrl, jsonFetchOptions(token), token);
  if (!firstResponse.ok) {
    const msg = await readApiErrorMessage(firstResponse, 'Unable to load organizations. Please try again.');
    throw new Error(msg);
  }

  const firstData = await parseJsonOrThrow(firstResponse, 'GET /org/');
  const firstRows = normalizeOrganizationRows(firstData);

  const isPaginated = Array.isArray(firstData?.results) || typeof firstData?.next === 'string';
  if (!isPaginated) {
    return firstRows;
  }

  const merged = [...firstRows];
  const seenKeys = new Set(merged.map((row, idx) => String(row?.id ?? row?.uu_id ?? `${idx}`)));
  let nextUrl = toSameOriginApiIfProxied(
    typeof firstData?.next === 'string' && firstData.next.trim() ? firstData.next.trim() : ''
  );
  if (!nextUrl) nextUrl = null;
  let page = 1;
  let safety = 0;

  while (nextUrl && safety < 50) {
    safety += 1;
    const response = await fetchWithAuthRetry(nextUrl, jsonFetchOptions(token), token);
    if (!response.ok) {
      const msg = await readApiErrorMessage(response, 'Unable to load organizations. Please try again.');
      throw new Error(msg);
    }

    const data = await parseJsonOrThrow(response, 'GET /org/');
    const rows = normalizeOrganizationRows(data);
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      const key = String(row?.id ?? row?.uu_id ?? `${page}-${index}`);
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      merged.push(row);
    }

    if (typeof data?.next === 'string' && data.next.trim()) {
      nextUrl = toSameOriginApiIfProxied(data.next.trim());
      page += 1;
      continue;
    }

    nextUrl = null;
  }

  return merged;
}

export const createOrganization = (payload, token = null) =>
  requestJson('POST', '/org/', payload, token);

export const updateOrganization = (payload, token = null) => {
  const uuId = extractAnyId(payload, ['uu_id', 'org_uuid', 'id']);
  const body = { ...payload };
  delete body.uu_id;
  delete body.id;
  delete body.org_uuid;
  return requestJson('PATCH', '/org/', body, token, { uu_id: uuId });
};

export const deleteOrganization = (payload, token = null) => {
  const uuId = extractAnyId(payload, ['uu_id', 'org_uuid', 'id']);
  return requestJson('DELETE', '/org/', null, token, { uu_id: uuId });
};

export const createSite = (payload, token = null) => {
  const siteName = String(payload?.site_name || payload?.name || '').trim();
  const siteDisc = String(payload?.site_disc || payload?.description || '').trim();
  const orgUuid = extractAnyId(payload, ['organization_uuid', 'org_uu_id', 'org_uuid']);

  return requestJson(
    'POST',
    '/site/',
    compactObject({ site_name: siteName, site_disc: siteDisc, organization_uuid: orgUuid || undefined }),
    token
  );
};

export const updateSite = (payload, token = null) => {
  const uuId = extractAnyId(payload, ['uu_id', 'site_uuid', 'id']);
  const siteName = String(payload?.site_name || payload?.name || '').trim();
  const siteDisc = String(payload?.site_disc || payload?.description || '').trim();
  const siteOwner = String(payload?.site_owner || payload?.details || '').trim();
  const siteStatus = String(payload?.site_status || payload?.status || '').trim();
  const organizationUuid = extractAnyId(payload, ['organization_uuid', 'org_uu_id', 'org_uuid']);
  const isActive =
    typeof payload?.is_active === 'boolean'
      ? payload.is_active
      : (typeof payload?.active === 'boolean' ? payload.active : undefined);
  return requestJson(
    'PATCH',
    '/site/',
    compactObject({
      site_name: siteName || undefined,
      site_disc: siteDisc,
      site_owner: siteOwner,
      site_status: siteStatus,
      organization_uuid: organizationUuid || undefined,
      is_active: isActive,
    }),
    token,
    { uu_id: uuId }
  );
};

export const deleteSite = (payload, token = null) => {
  const uuId = extractAnyId(payload, ['uu_id', 'site_uuid', 'id']);
  return requestJson('DELETE', '/site/', null, token, { uu_id: uuId });
};

export const createUserAdmin = (payload, token = null) => {
  const email = String(payload?.email || '').trim();
  const name = String(payload?.name || '').trim();
  const role = normalizeRoleToBackendRoleId(payload?.role);
  const organization = extractAnyId(payload, ['organization', 'organization_id', 'org_id', 'org_uuid', 'organization_uuid']);
  const timezone = String(payload?.timezone || 'UTC').trim() || 'UTC';
  const action = String(payload?.action || 'invite').trim();

  const query = compactObject({ email, name, role, organization, timezone });
  const body = compactObject({
    ...query,
    action,
    ...(action === 'add' && payload?.password ? { password: String(payload.password) } : {}),
    all_organizations_access: payload?.all_organizations_access,
    all_sites_access: payload?.all_sites_access,
    organization_access_uuids: payload?.organization_access_uuids,
    site_access_uuids: payload?.site_access_uuids,
  });
  return requestJson('POST', '/users/', body, token, query);
};

export const updateUserAdmin = (payload, token = null) => {
  const uuId = extractAnyId(payload, ['uu_id', 'user_uuid', 'id']);
  const roleId = normalizeRoleToBackendRoleId(payload?.role);
  const body = compactObject({
    name: payload?.name,
    role: roleId,
    timezone: payload?.timezone,
    all_organizations_access: payload?.all_organizations_access,
    all_sites_access: payload?.all_sites_access,
    organization_access_uuids: payload?.organization_access_uuids,
    site_access_uuids: payload?.site_access_uuids,
  });
  return requestJson('PATCH', '/users/', body, token, { uu_id: uuId });
};

export const deleteUserAdmin = (payload, token = null) => {
  const uuId = extractAnyId(payload, ['uu_id', 'user_uuid', 'id']);
  return requestJson('DELETE', '/users/', null, token, { uu_id: uuId });
};

export const resendUserInvitation = (payload, token = null) => {
  const uuId = extractAnyId(payload, ['uu_id', 'user_uuid', 'id']);
  if (!uuId) throw new Error('uu_id is required to resend invitation');
  return requestJson('POST', '/users/resend-invite/', null, token, { uu_id: uuId });
};

